import { C1_TechnicalViewIcon, C1_DinViewIcon } from "../../ConceptOne/src/plan/C1_DinSymbol";
import React, { useEffect, useRef, useState } from "react";
import { Eye, LibraryBig, FileSpreadsheet, Settings } from "lucide-react";
import { detect } from "detect-browser";

import "./Menubar.scss";
import { ProjectSelectionIcon } from "./ProjectSelectionIcon";

import { RedoButton, UndoButton, UndoDebugButton } from "./UndoRedoButtons";

import Point from "../models/Point";
import { createPortal } from "react-dom";

import { useOnChangeWatcher } from "../models/useOnChangeWatcher";
import { askToSaveProjectBeforeLeaving } from "./dialogs/SaveProjectBeforeLeaving";

import { ConnectionDisplayModes } from "../State";
import { saveProject } from "./SaveProject";
import { EventKey } from "../eventLogger/EventKeys";
import { askForGroupAddressUpdate } from "../addressGeneration/addressGenerator";
import BaseDialog from "../modelDialogs/BaseDialog";

import { useRedraw } from "../models/useRerender";
import Logger from "../Logger";
import BaseModel from "../models/BaseModel";
import Browser from "../browser";

import { showConsitencyErrorsDialog } from "./ConsitencyErrorsDialog";

import {
  enumUiMode,
  getMaxUiMode,
  getUiMode,
  isBetaVersion,
  isUiModeDeveloper,
  isUiModeExpert,
  uiModeWatcher,
  isUiModeProductAdmin,
} from "../uiMode";
//const EtsSyncAndExport = lazy(() => import("./EtsSyncAndExport"));
import EtsDialog from "./EtsDialog";
import Page from "../models/Page";
import { ShowRoomSubmittalsBackgroundInstantSet } from "./ShowRoomSubmittalsBackground";

import InstallDialog from "./dialogs/InstallDialog";
import SaveAsDialog from "./dialogs/SaveAsDialog";
import Debug from "./Debug";
import { itx, noItx } from "../translationItx/itx";

import CsvUploadDialog from "./dialogs/CsvUploadDialog";
import SyncManager from "../SyncManager";

import ServerDataBaseHandler from "../persistence/SeverDataBaseHandler";

import { FlyoutHost } from "./FlyoutControl";
import OfflineStatus from "./OfflineStatus";
import { useCommand } from "../models/useCommand";
import { isDevelopmentEnvironment } from "../estada-config";
import type Floor from "../models/Floor";
import PageGroup, { fixedPageGroupTags } from "../models/PageGroup";
import UploadFloorPlansDialog from "./UploadFloorPlansDialog";
import BaseObject, { globalOnChange } from "../models/BaseObject";
import SettingsDialog from "../modelDialogs/SettingsDialog";
import ReadbackDeviceDialog from "../readback/ReadbackDeviceDialog";
import GroupMonitorDialog2 from "../readback/GroupMonitorDialog2";
import ActiveParamsViewer from "../readback/ActiveParamsViewer";
import GroupMonitorDialog from "../modelDialogs/GroupMonitorDialog";
import { ExportState } from "../models/ProjectMetadata";
import Menubar_Extensions from "./Menubar_Extensions";
import { EtsConversionStatusOverlay, EtsPushStatusOverlay } from "../utils/EtsConversionStatusOverlay";
import ToShopLicensesDialog from "../modelDialogs/ToShopLicensesDialog";
import { etsEventEmitter } from "../etsFunctions/etsWebSocket";
import { isSyncActive } from "../etsFunctions/etsFunctions";
import { C1_APP_LOGO } from "../../ConceptOne/src/C1_Const";
import { useNavigate } from "react-router-dom";
import { C1_EditModeButtons } from "../../ConceptOne/src/components/C1_EditModeButtons";
import c1_state from "../../ConceptOne/src/model/C1_State";
import { C1_UiModeSelect } from "../../ConceptOne/src/components/C1_UiModeSelect";
import { isConnectionPlan } from "../connectionAnalyser/connectionAnalyserVisibility";
import { PdfExportDialog } from "../pdf/PdfExportDialog";
import { exportMainProjectPdf } from "../pdf/exportMainProjectPdf";
import { exportProjectExcel } from "../excel/exportProjectExcel";

const logger = new Logger();

const productAdminNavigationItems = [
  {
    title: "Product Admin", // no itx
    href: "/list-productadmin", // no itx
    icon: "las la-th-large", // no itx
    description: itx("Menubar.allProductAdmin", "All product admin screens wrapped in one interface."),
  },
  {
    title: "Product Wizard", // no itx
    href: "/productwizard", // no itx
    icon: "las la-magic", // no itx
    description: itx("Menubar.streamlinedStepbystepProduct", "Streamlined step-by-step product add/update workflow."),
  },
  {
    title: "Script Wizard", // no itx
    href: "/scriptwizard", // no itx
    icon: "las la-code", // no itx
    description: itx(
      "Menubar.collectProductSources",
      "Collect product sources, review allSignals and generate product scripts.",
    ),
  },
  {
    title: "Product Config List", // no itx
    href: "/list-productconfig", // no itx
    icon: "las la-code", // no itx
    description: itx("Menubar.browseCompareAnd", "Browse, compare and edit saved product configs."),
  },
  {
    title: "Product Catalog", // no itx
    href: "/upload-productdata", // no itx
    icon: "las la-boxes", // no itx
    description: "Upload KNX product data and maintain catalog metadata.",
  },
  {
    title: "Product Variant Store (Voltus)", // no itx
    href: "/upload-variantdata", // no itx
    icon: "las la-tags", // no itx
    description: itx("Menubar.uploadAndInspect", "Upload and inspect Voltus variant data."),
  },
  {
    title: "Image Store", // no itx
    href: "/upload-images", // no itx
    icon: "las la-images", // no itx
    description: "Upload and manage catalog product images.",
  },
];

function ProductAdminNavigationDialog() {
  return (
    <BaseDialog
      title="Product admin tools" // no itx
      noOkButton
      abortCaption={itx("menuBar.close", "Close")}
      style={{ width: 820, maxWidth: "94vw" }}
      bodyNoMinWidth
    >
      <div style={{ width: "min(760px, 88vw)", display: "flex", flexDirection: "column", gap: 12 }}>
        <div style={{ fontSize: 12, opacity: 0.68, lineHeight: 1.45 }}>
          Central navigation for product catalog, wizard, configuration, variants and image store screens.
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(230px, 1fr))", gap: 10 }}>
          {productAdminNavigationItems.map((item) => (
            <a
              key={item.href}
              href={item.href}
              target="_blank"
              rel="noopener noreferrer" // no itx
              onClick={() => state.dialogManager.closeDialog(true)}
              style={{
                display: "grid",
                gridTemplateColumns: "34px minmax(0, 1fr)",
                gap: 10,
                alignItems: "start",
                padding: 12,
                border: "1px solid rgba(0,0,0,0.1)",
                borderRadius: 8,
                background: "#fff",
                color: "inherit",
                textDecoration: "none",
                boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
              }}
            >
              <span
                style={{
                  width: 34,
                  height: 34,
                  borderRadius: 8,
                  display: "inline-flex",
                  alignItems: "center",
                  justifyContent: "center",
                  background: "rgba(229,126,4,0.1)",
                  color: "#e57e04",
                  fontSize: 18,
                }}
              >
                <i className={item.icon} />
              </span>
              <span style={{ minWidth: 0 }}>
                <span style={{ display: "block", fontSize: 14, fontWeight: 800 }}>{item.title}</span>
                <span style={{ display: "block", marginTop: 4, fontSize: 12, opacity: 0.72, lineHeight: 1.35 }}>
                  {item.description}
                </span>
                <span style={{ display: "block", marginTop: 8, fontSize: 11, opacity: 0.58 }}>{item.href}</span>
              </span>
            </a>
          ))}
        </div>
      </div>
    </BaseDialog>
  );
}

function openProductAdminNavigationDialog() {
  state.dialogManager.openDialog(<ProductAdminNavigationDialog />);
}

const mobile = Browser.isMobile();

declare const __APP_VERSION__: string;
declare const __APP_DATE__: string;

function preventFocus(event: any) {
  event.currentTarget.blur();
}

export function MenuGroup(props: any) {
  // right-click repeats the group's own click action (e.g. Home, Licenses), same as the tree's context menu
  function handleContextMenu(ev: React.MouseEvent) {
    if (!props.onClick) {
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    const label = typeof props.title === "string" ? props.title : "";
    state.contextMenuManager.show1(
      ev,
      { title: label, items: [{ id: props.id2, label, onClick: () => props.onClick() }] },
      [],
    );
  }

  return (
    <div
      className="menuGroup"
      id={"menuGroup_" + props.id2} // no itx
      style={{
        display: "flex",
        flexWrap: "nowrap",
        flexShrink: "1",
        flexGrow: "1",
        ...props.style,
      }}
      onClick={props.onClick}
      onContextMenu={handleContextMenu}
    >
      <div
        style={{
          display: "flex",
          flexDirection: "row",
          flexShrink: "1",
          flexGrow: "1",
          justifyContent: "space-evenly",
          alignContent: "space-evenly",
          width: "100%",
        }}
      >
        {props.children}
      </div>
      {!mobile && <div className="menuGroupText">{props.title}</div>}
    </div>
  );
}

export type ButtonEvent = { id: string; inputId?: string };

// shared list so the flyout popup and its right-click context menu show identical options
function getConnectionModeOptions(): { mode: ConnectionDisplayModes; label: string }[] {
  return [
    { mode: ConnectionDisplayModes.none, label: itx("menubar.no", "Keine") },
    { mode: ConnectionDisplayModes.all, label: itx("menubar.all", "Alle") },
    { mode: ConnectionDisplayModes.channels, label: itx("menubar.channels", "Kanäle") },
    { mode: ConnectionDisplayModes.buttons, label: itx("menubar.buttons", "Taster") },
    { mode: ConnectionDisplayModes.scenes, label: itx("menubar.scenesAndMultiplexer", "Scene & Multiplexer") },
  ];
}

function selectConnectionMode(mode: ConnectionDisplayModes) {
  state.selectionManager.selectedObject = undefined;
  state.connectionDisplayMode.value = mode;
}

/** Shares the C1 presentation selection while using the main app's menu buttons. */
function C1PlanViewMenuGroup() {
  return (
    <MenuGroup title={itx("menuBar.planPresentation", "Plan-Darstellung")}>
      <C1_UiModeSelect
        renderButton={(option) => (
          <SimpleMenuButton
            key={option.value}
            id={`plan-view-${option.value}`}
            pressed={option.active}
            pressedBackground={false}
            text={option.label}
            title={option.label}
            icon={React.createElement(
              option.value === "design" ? Eye : option.value === "din" ? C1_DinViewIcon : C1_TechnicalViewIcon,
              {
                className: "menuIcon",
                "aria-hidden": true,
                style: { color: option.active ? "var(--knxColorGreen)" : "#000" },
              },
            )}
            onClick={option.select}
          />
        )}
      />
    </MenuGroup>
  );
}

/** Keeps the C1 edit modes available regardless of the active main-app tab. */
function C1EditModeMenuGroup() {
  useOnChangeWatcher(uiModeWatcher);
  useOnChangeWatcher(state.projectMetadata, property => property === "isC1Project");
  useOnChangeWatcher(state.activePanes);
  useOnChangeWatcher(c1_state.editMode);
  useOnChangeWatcher(state.connectionAnalyser.manuallyEnabled);

  const activeItem = state.activePanes.getActiveItem()?.item;
  const activeItemClassName = activeItem?.className;
  const containsRoom = activeItemClassName === noItx("Room");
  const containsFloor = activeItemClassName === noItx("Floor");

  useEffect(() => {
    if (!state.projectMetadata.isC1Project) return;
    if (containsRoom && c1_state.editMode.value === "furnishing") {
      state.dockManager.openPalette();
      return;
    }
  }, [containsFloor, containsRoom]);

  if (!state.projectMetadata.isC1Project) return null;

  return (
    <MenuGroup title={itx("menuBar.editMode", "Bearbeitungsmodus")}>
      <C1_EditModeButtons
        electricalMode="EloDetail" // no itx
        renderButton={(button) =>
          button.mode === "interactionConcept" ? null : (
            <SimpleMenuButton
              key={button.mode}
              id={`c1-edit-mode-${button.mode}`}
              text={button.label}
              title={button.label}
              pressed={button.active}
              pressedBackground={false}
              icon={<span className={`mainAppEditModeIcon${button.active ? " mainAppEditModeIcon--active" : ""}`}>{button.icon}</span>}
              onClick={button.select}
            />
          )
        }
      />
    </MenuGroup>
  );
}

function ConnectionRadioRow(props: { mode: ConnectionDisplayModes; text: string }) {
  const { text, mode } = props;

  let cls = "radioButton"; // no itx
  if (state.connectionDisplayMode.value == mode) {
    cls += " active";
  }
  return (
    <div className="readioButtonLine" onClick={() => selectConnectionMode(mode)}>
      <div className={cls} />
      {text}
    </div>
  );
}

function ConnectionSelection() {
  useOnChangeWatcher(state.connectionDisplayMode);
  return (
    <div className="App PropertyView">
      <div className="ObjectPropertiesHeader">{itx("menubar.showConnections", "Verbingungen anzeigen")}</div>

      <div style={{ padding: "10px", display: "flex", flexDirection: "column" }}>
        <div style={{ paddingBottom: "10px" }}>
          {itx("menubar.showFollowingConnections", "Die folgenden Verbinungen sollen angezeigt weden")}
        </div>

        {getConnectionModeOptions().map((option) => (
          <ConnectionRadioRow key={option.mode} text={option.label} mode={option.mode} />
        ))}
      </div>
    </div>
  );
}

// shared list so the flyout popup and its right-click context menu show identical options
function getUiModeOptions(): { value: enumUiMode; label: string }[] {
  const userRoleModes = [
    {
      value: enumUiMode.user,
      label: itx("SettingsScreen.user", "Anwender"),
    },
    {
      value: enumUiMode.expert,
      label: itx("SettingsScreen.expert", "Experte"),
    },
  ];

  if (getMaxUiMode() >= enumUiMode.productAdmin || isDevelopmentEnvironment()) {
    userRoleModes.push({
      value: enumUiMode.productAdmin,
      label: itx("menubar.ProdAdmin", "Product Administrator"),
    });
  }

  if (getMaxUiMode() >= enumUiMode.admin || isDevelopmentEnvironment()) {
    userRoleModes.push({
      value: enumUiMode.admin,
      label: itx("menubar.admin", "Administrator"),
    });
  }

  if (isDevelopmentEnvironment()) {
    userRoleModes.push({
      value: enumUiMode.developer,
      label: itx("SettingsScreen.developer", "Entwickler"),
    });
  }
  return userRoleModes;
}

function selectUiMode(mode: enumUiMode) {
  state.setUiMode(mode);
  if (isUiModeProductAdmin()) {
    const appElm = document.getElementById("App");
    if (appElm) {
      appElm.style.userSelect = "text"; // no itx
    }
  }
  state.flyoutMenuVisible.value = "";
}

function UiModeRadioRow(props: { mode: enumUiMode; text: string }) {
  const { text, mode } = props;

  let cls = "radioButton"; // no itx
  if (state.uiMode.value == mode) {
    cls += " active";
  }
  return (
    <div className="readioButtonLine" onClick={() => selectUiMode(mode)}>
      <div className={cls} />
      {text}
    </div>
  );
}

function UiModeSelection() {
  useOnChangeWatcher(state.uiMode);
  const rows = getUiModeOptions().map((option) => (
    <UiModeRadioRow key={option.value} text={option.label} mode={option.value} />
  ));

  return (
    <div className="App PropertyView">
      <div className="ObjectPropertiesHeader">{itx("menubar.changeUserRole", "Benutzerrolle wechseln")}</div>

      <div style={{ padding: "10px", display: "flex", flexDirection: "column" }}>
        <div style={{ paddingBottom: "10px" }}>
          {itx("menubar.changeToUserRole", "Zur angegebenen Beutzerrolle wechseln")}
        </div>

        {rows}
      </div>
    </div>
  );
}

function FlyoutMenuButton(props: {
  id: string;
  icon?: any;
  iconStyle?: any;
  iconName?: string;
  text?: string;
  content: any;
  // shown as heading + selectable entries in the right-click context menu of this button
  contextMenuTitle?: string;
  contextMenuItems?: SimpleMenuButtonContextMenuItem[];
}): any {
  const { id, icon, iconName, iconStyle, text, content, contextMenuTitle, contextMenuItems } = props;

  useOnChangeWatcher(state.flyoutMenuVisible);

  const flyOutPosition = useRef<any>(undefined);

  return (
    <div>
      {state.flyoutMenuVisible.value === id + "_flyout" &&
        createPortal(
          <FlyoutHost
            flyOutPosition={flyOutPosition.current}
            flyOutContent={content}
            onClose={() => {
              state.flyoutMenuVisible.value = "";
            }}
            flyoutId={id + "_flyout"}
          />,
          document.body,
        )}

      <SimpleMenuButton
        id={"menuFlyout" + id} // no itx
        icon={icon}
        iconName={iconName}
        iconStyle={iconStyle}
        text={text}
        contextMenuTitle={contextMenuTitle}
        contextMenuItems={contextMenuItems}
        onClick={(ev) => {
          ev.preventDefault();
          ev.stopPropagation();

          const elm = document.getElementById("menuFlyout" + id);
          if (elm) {
            const r = elm.getBoundingClientRect();
            if (r) {
              const p = new Point(r.left, r.bottom - 10);
              flyOutPosition.current = p;
              state.flyoutMenuVisible.value = id + "_flyout";
            }
          }
        }}
      />
    </div>
  );
}

export type SimpleMenuButtonContextMenuItem = {
  id?: string;
  label: string;
  iconName?: string;
  icon?: any;
  onClick: () => void;
};

export function SimpleMenuButton(props: {
  icon?: any;
  command?: string;
  iconName?: string;
  iconStyle?: any;
  text?: string;
  title?: string;
  tooltip?: string;
  disabled?: boolean;
  inputType?: string;
  status?: boolean;
  pressed?: boolean;
  pressedBackground?: boolean;
  onClick?: (ev) => void;
  children?: any;
  id?: string;
  href?: string;
  // items shown in a context menu (same mechanism as the tree views) when right-clicking the button
  contextMenuItems?: SimpleMenuButtonContextMenuItem[];
  contextMenuTitle?: string;
}) {
  let {
    icon,
    command,
    iconName,
    iconStyle,
    id,
    text,
    title,
    tooltip,
    disabled,
    inputType,
    status,
    onClick,
    children,
    href,
    contextMenuItems,
    contextMenuTitle,
  } = props;

  const showTimer = useRef<any>(0);

  // fall back to a single item mirroring the left-click action so every button gets a right-click menu
  function getEffectiveContextMenuItems(): SimpleMenuButtonContextMenuItem[] | undefined {
    if (contextMenuItems && contextMenuItems.length > 0) {
      return contextMenuItems;
    }
    if (!onClick || disabled) {
      return undefined;
    }
    const clickHandler = onClick;
    return [
      {
        id: command || id,
        label: (typeof text === "string" && text) || title || command || id || "",
        iconName,
        icon,
        onClick: () => clickHandler({ preventDefault: () => {}, stopPropagation: () => {} } as any),
      },
    ];
  }

  function handleContextMenu(ev: React.MouseEvent) {
    const items = getEffectiveContextMenuItems();
    if (!items || items.length === 0) {
      return;
    }
    ev.preventDefault();
    ev.stopPropagation();
    const menuTitle = contextMenuTitle ?? (typeof text === "string" ? text : undefined);
    state.contextMenuManager.show1(ev, { title: menuTitle, items }, []);
  }

  function highlightClickedButtonOnTime(event: any) {
    const element = event.currentTarget as HTMLElement;

    element.classList.add("pressed"); // no itx
    if (showTimer.current) {
      clearTimeout(showTimer.current);
    }
    showTimer.current = setTimeout(() => {
      showTimer.current = 0;
      element.classList.remove("pressed"); // no itx
    }, 500);
  }

  function clicked(ev: any) {
    logger.log("clicked SimpleMenuButton " + command);
    highlightClickedButtonOnTime(ev);

    // handle subscriptions to commands

    if (globalMenubar) {
      const menuButton = globalMenubar.querySelector("#" + command);
      if (menuButton) {
        // test
        const executeEvent = new CustomEvent<ButtonEvent>(noItx("execute"), {
          detail: { id: command || "" },
          bubbles: true,
          cancelable: true,
          composed: false,
        });
        menuButton.dispatchEvent(executeEvent);
      }
    }
    if (onClick) {
      onClick(ev);
    }
    let menuId = command || id || "";
    if (command?.includes(".")) {
      menuId = itx(command, command);
    }
    state.eventLogger.addEvent(EventKey.MenuItemClicked, {
      menuId: menuId,
    });
  }

  if (icon == undefined && iconName) {
    icon = <i className={`menuIcon las la-${iconName}`} style={iconStyle}></i>;
  }

  const style: any = {
    flexShrink: "1",
    flexGrow: "1",
    position: "relative",
    margin: "2px",
    padding: "2px",
    width: mobile ? "37px" : "47px", // no itx
    height: mobile ? "30px" : "50px",
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    color:
      status == undefined
        ? disabled
          ? "grey"
          : "var(--knxColorBlack)"
        : status
          ? "var(--knxColorGreen)"
          : "var(--knxColorLightGrey)", // no itx
  };

  return (
    <div className="menuButton" id={id}>
      {children}
      {children == undefined && !href && (
        <button
          id={command}
          aria-pressed={props.pressed}
          tabIndex={-1}
          onFocus={preventFocus}
          disabled={disabled}
          title={title}
          data-tooltip-id={tooltip ? "tv-tooltip" : undefined}
          data-tooltip-content={tooltip}
          data-tooltip-place={tooltip ? "bottom" : undefined}
          style={{ ...style, backgroundColor: props.pressed && props.pressedBackground !== false ? "var(--selectedBackgroundColor, #d9d79e)" : undefined }}
          className="btn"
          onClick={(ev) => {
            if (status == undefined || status) {
              clicked(ev);
            }
          }}
          onContextMenu={handleContextMenu}
        >
          {icon}
        </button>
      )}

      {children == undefined && href && (
        <a
          id={command}
          tabIndex={-1}
          onFocus={preventFocus}
          href={href}
          title={title}
          data-tooltip-id={tooltip ? "tv-tooltip" : undefined}
          data-tooltip-content={tooltip}
          data-tooltip-place={tooltip ? "bottom" : undefined}
          style={style}
          className="btn"
          onClick={(ev) => {
            if (status == undefined || status) {
              clicked(ev);
            }
          }}
          onContextMenu={handleContextMenu}
        >
          {icon}
        </a>
      )}

      <div className="menuText">
        <div
          style={{
            width: "100px",
            marginLeft: "-49px",
            position: "absolute",
            textAlign: "center",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          {text}
        </div>
      </div>
    </div>
  );
}

export function openProgrammingPane() {
  if (!isUiModeProductAdmin()) {
    return false;
  }

  const obj = state.topologies;
  if (state.activePanes.isOpen(obj.id)) {
    state.activePanes.activatePane(obj.id);
  } else {
    state.activePanes.openPane(obj);
    const pane = state.activePanes.getPane(obj.id);
    if (pane) {
      pane.title = itx("menubar.programmingDevices", "Programmiere Geräte");

      if (pane.tabItemData) {
        pane.tabItemData.title = pane.title || "";
      }
    }
  }
  return true;
}

function openReadbackDeviceDialog() {
  state.dialogManager.openDialog(<ReadbackDeviceDialog />);
}

function onCut(
  taste?: "del" | "strg_x", // no itx
) {
  // main drawing Area check if the Room should be deleted
  const selectedElem = state.selectionManager.selectedObject;
  const withCopy = taste == "strg_x" || taste == "del" ? false : true; // no itx
  state.menubarActions.onDelete(selectedElem, withCopy);
}

async function onEdit() {
  const selectedObject = state.selectionManager.selectedObject;
  if (selectedObject) {
    // logger.log(`className ${selectedObject.className}`);
    if (
      selectedObject.className == "Button" ||
      selectedObject.className == "Actor" ||
      // selectedObject.className == "Scene" ||
      selectedObject.className == "DeviceTemplate"
    ) {
      selectedObject.treeViewModel.handleOpen();
    } else if (selectedObject.className == "ButtonChannel" || selectedObject.className == "Channel") {
      (selectedObject.parent as BaseModel)?.treeViewModel?.handleOpen();
    }
  }
}

export function Space() {
  return <div style={{ width: "10px" }}></div>;
}

// <SimpleMenuButton text="Edit" icon="✏️" />
// unicode icons:  https://unicode-table.com/en/

export function subscribeMenuCommand(command: string, callback: (event: CustomEvent<ButtonEvent>) => void) {
  logger.log("subscribeMenuCommand " + command);

  if (globalMenubar) {
    const menuButton = globalMenubar.querySelector("#" + command);
    if (menuButton) {
      menuButton.addEventListener("execute", callback);
    }
  }
}

export function unsubscribeMenuCommand(command: string, callback: (event: CustomEvent<ButtonEvent>) => void) {
  if (globalMenubar) {
    const menuButton = globalMenubar.querySelector("#" + command);
    if (menuButton) {
      menuButton.removeEventListener("execute", callback);
    }
  }
}

var globalMenubar: any = null;

async function onClickSave() {
  if (state.isDirty) {
    state.projectMetadata.readOnly = false;
  }
  saveProject();
}

function formatRelativeAutobackupTime(timestamp: number): string {
  const differenceSeconds = (timestamp - Date.now()) / 1000;
  const absoluteSeconds = Math.abs(differenceSeconds);
  const relativeTime = new Intl.RelativeTimeFormat(undefined, { numeric: "auto" }); // no itx

  if (absoluteSeconds < 60) return relativeTime.format(Math.round(differenceSeconds), "second"); // no itx
  if (absoluteSeconds < 60 * 60) return relativeTime.format(Math.round(differenceSeconds / 60), "minute"); // no itx
  if (absoluteSeconds < 24 * 60 * 60) return relativeTime.format(Math.round(differenceSeconds / 3600), "hour"); // no itx
  return relativeTime.format(Math.round(differenceSeconds / (24 * 3600)), "day"); // no itx
}

export function globalOnKeydown(ev: any) {
  //
  // Wenn wir ein actives element haben,
  // (das heißt "diese Element hat den Focus")
  // muss es sich um ein Input Element oder editiebaren div handeln
  // dann soll die Löschtaste zb nicht ein Objekt löschen sondern nur
  // die "normale" funktion im Editfeld haben
  //

  if (document.activeElement) {
    if (document.activeElement.hasAttribute("contentEditable")) {
      return;
    }

    if (document.activeElement.tagName == "INPUT") {
      return;
    }

    if (document.activeElement.tagName == "TEXTAREA") {
      return;
    }

    // logger.log("focus element: ", document.activeElement);
    // return;
  }

  // function to check the detection
  ev = ev || window.event; // Event object 'ev'
  const key = ev.which || ev.keyCode; // Detecting keyCode

  // Detecting Ctrl
  let ctrl = ev.ctrlKey ? ev.ctrlKey : key === 17 ? true : false;
  ctrl = ctrl || ev.metaKey;

  if (key == 86 && ctrl) {
    // If key pressed is V and if ctrl is true.
    state.menubarActions.onPaste();
    // onPaste();
  } else if (key == 67 && ctrl) {
    // If key pressed is c and if ctrl is true.
    state.menubarActions.onCopy();
    //onCopy();
    // } else if (key == 88 && ctrl) {
    //   // If key pressed is X and if ctrl is true.
    //   onCut("strg_x"); // no itx
  } else if (key == 46 && !ctrl) {
    // If key pressed is X and if ctrl is false.//if pressed del
    state.menubarActions.onDelete();
  } else if (key == 90 && ctrl) {
    // If key pressed is Z and if ctrl is true.
    state.undoManager.undo();
  } else if (key == 89 && ctrl) {
    // If key pressed is Y and if ctrl is true.
    state.undoManager.redo();
  } else if (key == 83 && ctrl) {
    // If key pressed is S and if ctrl is true.
    ev.preventDefault();
    onClickSave();
  }
}

export default function Menubar() {
  const navigate = useNavigate();
  const menubarRef = useRef<HTMLDivElement>(null);
  const [showScrollLeft, setShowScrollLeft] = useState(false);
  const [pdfExportOpen, setPdfExportOpen] = useState(false);
  const [excelExportOpen, setExcelExportOpen] = useState(false);
  const [showScrollRight, setShowScrollRight] = useState(false);
  const [etsExportState, setEtsExportState] = useState<ExportState>(
    state.projectMetadata.exportState || ExportState.None,
  );
  const [etsPushActive, setEtsPushActive] = useState(isSyncActive());
  const [browserName, setBrowserName] = useState("");
  const [browserVersion, setBrowserVersion] = useState("");
  const [, setAutobackupTimeRefresh] = useState(0);

  let openRoomPlan = state.activePanesOfCurrentScreen.openRoomOrFloorPlan();
  if (state.settings.diagnosticMode) {
    openRoomPlan = undefined;
  }

  const backgroundImageFromFloorPlan = openRoomPlan?.isBackgroundImageFromFloorPlan;

  // sync export state
  useEffect(() => {
    const syncInterval = setInterval(() => {
      if (state.projectMetadata.exportState !== etsExportState) {
        setEtsExportState(state.projectMetadata.exportState);
      }
    }, 1000);
    return () => clearInterval(syncInterval);
  }, [etsExportState]);

  useEffect(() => {
    const refreshTimer = setInterval(() => setAutobackupTimeRefresh((value) => value + 1), 30 * 1000);
    return () => clearInterval(refreshTimer);
  }, []);

  useEffect(() => {
    const handleSyncProgressChange = (active: boolean) => {
      setEtsPushActive(Boolean(active));
    };

    etsEventEmitter.on("syncProgressChange", handleSyncProgressChange); // no itx
    setEtsPushActive(isSyncActive());
    return () => {
      etsEventEmitter.off("syncProgressChange", handleSyncProgressChange); // no itx
    };
  }, []);

  useEffect(() => {
    const menuBar = menubarRef.current;
    if (!menuBar) return;

    const updateScrollButtons = () => {
      setShowScrollLeft(menuBar.scrollLeft > 0);
      setShowScrollRight(menuBar.scrollLeft + menuBar.clientWidth < menuBar.scrollWidth - 1);
    };

    const onWheel = (e: WheelEvent) => {
      if (e.deltaY !== 0 && menuBar.scrollWidth > menuBar.clientWidth) {
        e.preventDefault();
        menuBar.scrollLeft += e.deltaY;
      }
    };

    menuBar.addEventListener("wheel", onWheel, { passive: false });
    menuBar.addEventListener("scroll", updateScrollButtons);

    const resizeObserver = new ResizeObserver(updateScrollButtons);
    resizeObserver.observe(menuBar);
    updateScrollButtons();

    return () => {
      menuBar.removeEventListener("wheel", onWheel);
      menuBar.removeEventListener("scroll", updateScrollButtons);
      resizeObserver.disconnect();
    };
  }, []);

  useEffect(() => {
    globalMenubar = menubarRef.current;

    document.body.addEventListener("keydown", globalOnKeydown);

    addEventListener("storage", onLocalStorageChanged);

    const browser = detect();
    if (browser) {
      setBrowserName(browser.name);
      if (browser.version) {
        setBrowserVersion(browser.version);
      }
    }

    const syncTimer = setInterval(async () => {
      try {
        if (await ServerDataBaseHandler.serverIsOnline) {
          if (state.projectMetadata.id !== "againInProjectSelection") {
            const reloaded = await state.projectMetadata.updateFromServer();
            if (reloaded && state.projectMetadata.checkIfProjectIsModifiedOnServerSinceCopiedLocal()) {
              redraw();
            }
          }
        }
      } catch (ex) {
        logger.error(ex);
      }
    }, 60000);

    const someObjectChangedHandler = globalOnChange.subscribe(
      (changedObject: BaseObject, changedProperty: string | any | undefined) => {
        if (changedObject.className == "Page") {
          redraw();
        }
      },
    );

    return () => {
      globalOnChange.unsubscribe(someObjectChangedHandler);

      clearInterval(syncTimer);
      document.body.removeEventListener("keydown", globalOnKeydown);
      removeEventListener("storage", onLocalStorageChanged);
    };
  }, []);

  function onLocalStorageChanged(this: Window, ev: StorageEvent) {
    if (ev.key == "clipboard") {
      logger.log("clipboard changed");
      redraw();
    }
  }

  useCommand(
    "updateMenubar", // no itx
    () => {
      return true;
    },
  ); // redraw

  useOnChangeWatcher(state.selectionManager, (changedProperty, changedObject) => {
    return true;
  });
  useOnChangeWatcher(state.clipboard);
  useOnChangeWatcher(state.c1.editMode);
  useOnChangeWatcher(state.settings);
  useOnChangeWatcher(state.uiMode);
  useOnChangeWatcher(state._showModifiedStatus);
  useOnChangeWatcher(state._lastAutobackupCreatedAt);

  useOnChangeWatcher(state.activePanes);
  useOnChangeWatcher(state.activePanes2);
  useOnChangeWatcher(state.connectionDashboardMode);
  useOnChangeWatcher(state.connectionAnalyser.selectedVirtualConnectionId);
  useOnChangeWatcher(state.connectionAnalyser.endpoints);

  const redraw = useRedraw();
  const lastAutobackupCreatedAt = state.lastAutobackupCreatedAt;
  const saveTooltip = lastAutobackupCreatedAt
    ? state.translate("menuBar.saveTooltipWithAutobackup", "Speichern\nAuto-Backup vorhanden – zuletzt T1 erstellt.", {
        T1: formatRelativeAutobackupTime(lastAutobackupCreatedAt),
      })
    : state.translate("menuBar.save", "Speichern");

  function openProjectSelection() {
    askToSaveProjectBeforeLeaving("/");
  }

  function onClickGenerateAdresses() {
    askForGroupAddressUpdate(false);
  }

  function openDebugDialog() {
    state.dialogManager.openDialog(<Debug />, false, false);
  }

  function openEtsDialog() {
    state.dialogManager.openDialog(<EtsDialog />, true);
  }

  function openEtsDiagnosticViewer() {
    const windowWidth = window.innerWidth - 200 || 900;
    state.dialogManager.openDialog(<GroupMonitorDialog windowWidth={windowWidth} />);
  }

  function openDiagnosticViewer() {
    const windowWidth = window.innerWidth - 200 || 900;
    // state.dialogManager.openDialog(<GroupMonitorDialog windowWidth={windowWidth} />);
    state.dialogManager.openDialog(<GroupMonitorDialog2 />);
  }

  function openReadbackMemoryViewer() {
    state.dialogManager.openDialog(<ActiveParamsViewer xmlString="" paramTableHex="" absoluteSegmentId="" />);
  }

  function openEtsLogViewer() {
    const windowWidth = window.innerWidth - 200 || 900;

    state.dialogManager.openDialog(
      <BaseDialog
        title={state.translate("menuBar.etsLogHistorie", `ETS Log History`)}
        noOkButton={true}
        onClose={() => {
          return true;
        }}
      >
        <table className="logTable" id="logTable" style={{ width: `${windowWidth}px` }}>
          <thead>
            <tr
              style={{
                color: "var(--knxColorWhite)",
                backgroundColor: "var(--knxColorGrey)",
              }}
            >
              <th style={{ width: "25%", paddingLeft: "10px" }}>{state.translate("menuBar.timestamp", "Timestamp")}</th>
              <th>{state.translate("description", "Description")}</th>
            </tr>
          </thead>
          <tbody>
            {state.projectMetadata.etsLog.length == 0 && (
              <tr>
                <td colSpan={2} style={{ textAlign: "center" }}>
                  {state.translate("menuBar.noLogEntriesTodisplay", " ------ No log entries to display. ------")}
                </td>
              </tr>
            )}
            {state.projectMetadata.etsLog.length > 0 &&
              state.projectMetadata.etsLog.map((logEntry: any, index: number) => (
                <tr key={index}>
                  <td>{logEntry.Timestamp}</td>
                  <td>{logEntry.Description}</td>
                </tr>
              ))}
          </tbody>
        </table>
      </BaseDialog>,
    );
  }

  function isDisabled(): boolean {
    if (state.selectionManager.selectedObject == undefined) {
      return true;
    } else {
      return false;
    }
  }

  function cutIsDisabled(): boolean {
    const selectedObj = state.selectionManager.selectedObject;
    if (state.c1.isDeletionProtectedInDetailMode(selectedObj)) return true;
    if (selectedObj?.canBeDeletedInteractive() && selectedObj?.canBeCopiedInteractive()) {
      return false;
    } else {
      return true;
    }
  }

  function deleteIsDisabled(): boolean {
    if (state.connectionAnalyser.selectedVirtualConnection) return false;
    const selectedObj = state.selectionManager.selectedObject;
    if (state.c1.isDeletionProtectedInDetailMode(selectedObj)) return true;

    if (!selectedObj?.canBeDeletedInteractive()) {
      if (selectedObj?.getDeleteConfirmText) {
        return false;
      } else {
        return true;
      }
    } else {
      return false;
    }
  }

  function copyIsDisabled(): boolean {
    const selectedObj = state.selectionManager.selectedObject;
    if (selectedObj?.canBeCopiedInteractive()) {
      return false;
    } else {
      return true;
    }
  }

  function pasteIsDisabled(): boolean {
    const selectedObj = state.selectionManager.selectedObject;
    const classNameCopied = state.clipboard.getClassName();

    if (classNameCopied && selectedObj?.treeViewModel.canBePastedInteractive(classNameCopied)) {
      return false;
    } else {
      return true;
    }
  }

  function isEditible(): boolean {
    if (
      state.selectionManager.selectedObject == undefined ||
      !state.selectionManager.selectedObject.canBeEditedInteractive()
    ) {
      return false;
    } else {
      return true;
    }
  }
  function isAdjustable(): boolean {
    const page = state.getActivePage();

    if (page?.backgroundImgUrl || backgroundImageFromFloorPlan) {
      return true;
    } else {
      return false;
    }
  }

  function CheckBrowser(props: { children: any }) {
    const { children } = props;

    // handle the case where we don't detect the browser

    if (browserName == "safari" && browserVersion < "15.4") {
      return (
        <div className="menuGroup" style={{ backgroundColor: "red", padding: "10px" }}>
          {state.translate("menuBar.suportSafari", "ESTADA unterstützt Safari erst ab Version 15.4")}
          <br />
          {state.translate("menuBar.installed", "installiert")}: {browserVersion}
        </div>
      );
    } else return <div>{children}</div>;
  }

  ///////////////////////////////////////////////////////////

  let etsTitle = "ETS"; // no itx

  switch (etsExportState) {
    case ExportState.Failure:
      etsTitle = itx("Menubar.conversionfailed", "Conversion failed.");
      break;

    case ExportState.WaitingForUploadReady:
    case ExportState.WaitingForConversion:
      etsTitle = itx("Menubar.etsconversionin", "ETS Conversion in progress...");
      break;

    case ExportState.WaitingForDownload:
      etsTitle = itx("Menubar.etsconversionfile", "ETS Conversion file is ready.");
      break;
  }
  if (etsPushActive) {
    const pushTitle = itx("Menubar.etsPushRunning", "Push to ETS is running.");
    etsTitle = etsTitle === "ETS" ? pushTitle : `${etsTitle}\n${pushTitle}`; // no itx
  }

  function scrollMenubar(direction: "left" | "right") {
    const menuBar = menubarRef.current;
    if (menuBar) {
      menuBar.scrollBy({ left: direction === "left" ? -200 : 200, behavior: "smooth" });
    }
  }

  return (
    <div className="menuBarWrapper" id="menuBarWrapper">
      {(pdfExportOpen || excelExportOpen) &&
        createPortal(
          <div className="C1" style={{ display: "contents" }}>
            <PdfExportDialog
              mode="main" // no itx
              open={pdfExportOpen || excelExportOpen}
              format={
                excelExportOpen
                  ? "excel" // no itx
                  : "pdf" // no itx
              }
              onClose={() => {
                setPdfExportOpen(false);
                setExcelExportOpen(false);
              }}
              onExport={(config, mode, chapterId) =>
                excelExportOpen
                  ? exportProjectExcel(state, config, mode, chapterId)
                  : exportMainProjectPdf(state, config, mode, chapterId)
              }
            />
          </div>,
          document.body,
        )}
      {showScrollLeft && (
        <button className="menuBarScrollBtn menuBarScrollBtn--left" onClick={() => scrollMenubar("left")}>
          <i className="las la-angle-left" />
        </button>
      )}
      {showScrollRight && (
        <button className="menuBarScrollBtn menuBarScrollBtn--right" onClick={() => scrollMenubar("right")}>
          <i className="las la-angle-right" />
        </button>
      )}
      <div className="menuBar" ref={menubarRef} id="menuBar">
        <MenuGroup
          style={{
            backgroundColor: "transparent",
          }}
          onClick={() => {
            openProjectSelection();
          }}
        >
          <SimpleMenuButton
            id="main-project-selection"
            tooltip={itx("menuBar.projectSelection", "Zur Projektauswahl")}
            icon={
              <ProjectSelectionIcon
                logo={`${state.hostBaseUrl}images/logo-detail-small-2.png`}
                logoScale={0.765}
                logoOffsetY={4}
              />
            }
          />
        </MenuGroup>

        {state.projectMetadata.isC1Project && isUiModeDeveloper() && (
          <MenuGroup
            style={{
              backgroundColor: "var(--primary-color)",
              color: "var(--knxColorWhite)",
            }}
            onClick={() => {
              navigate("/concept-one");
            }}
          >
            <SimpleMenuButton
              tooltip={itx("menuBar.conceptOne", "Concept One")}
              icon={
                <img
                  src={C1_APP_LOGO}
                  alt=""
                  aria-hidden="true"
                  style={{ display: "block", width: "40px", height: "40px", objectFit: "contain" }}
                />
              }
            />
          </MenuGroup>
        )}

        <MenuGroup id2="licenses">
          <SimpleMenuButton
            id="project-license-button" // no itx
            text={itx("menuBar.license", "Lizenz")}
            tooltip={itx("menuBar.licenses", "Lizenzen")}
            onClick={() => state.dialogManager.openDialog(<ToShopLicensesDialog />)}
            icon={
              <svg className="menuIcon" width="28" height="28" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
                <path
                  d="M5 7H19L20 21H4Z" // no itx
                  fill="#e57e04" // no itx
                  stroke="#59636e" // no itx
                  strokeWidth="1.2" // no itx
                  strokeLinejoin="round" // no itx
                />
                <path
                  d="M9 9V5A3 3 0 0 1 15 5V9" // no itx
                  fill="none" // no itx
                  stroke="#59636e" // no itx
                  strokeWidth="1.2" // no itx
                  strokeLinecap="round" // no itx
                />
              </svg>
            }
          />
        </MenuGroup>
        <MenuGroup title={state.translate("menuBar.file", "File ")}>
          <OfflineStatus />
          {state.projectMetadata.checkIfProjectIsModifiedOnServerSinceCopiedLocal() && (
            <SimpleMenuButton
              onClick={async () => {
                const sm = new SyncManager(state.projectMetadata);
                await sm.mergeIncomingVersionFromServer();

                state.dialogManager.openDialog(
                  <BaseDialog
                    title={itx("menubar.syncronisation", "Synchronisierung")}
                    noAbortButton={true}
                    hideXButton={true}
                    onOk={() => {
                      redraw();
                      return true;
                    }}
                    onClose={() => {
                      redraw();
                      return true;
                    }}
                  >
                    <div style={{ padding: "20px" }}>
                      {itx(
                        "menubar.syncronisationHasBeenCariedOut",
                        "Die Synchronisierung des Projekts mit dem aktuellen Cloud-Version wurde durchgeführt",
                      )}
                    </div>
                  </BaseDialog>,
                );
              }}
              text={itx("menubar.update", "Update")}
              iconName="sync" // no itx
              disabled={false}
              title={itx(
                "menubar.syncronisationWillBeCariedOutFromCloud",
                "Mit der aktuellen Version des Projekts aus der Cloud synchronisieren",
              )}
            />
          )}
          <SimpleMenuButton
            onClick={() => {
              onClickSave();
            }}
            command="save" // no itx
            text={state.translate("menuBar.save", "Save")}
            title={saveTooltip}
            contextMenuTitle={state.translate("menuBar.save", "Save")}
            contextMenuItems={[
              {
                id: "save", // no itx
                label: state.translate("menuBar.save", "Save"),
                iconName: "save", // no itx
                onClick: () => onClickSave(),
              },
              {
                id: "saveAs", // no itx
                label: itx("menubar.saveAs", "Speichern unter"),
                iconName: "angle-down", // no itx
                onClick: () => state.dialogManager.openDialog(<SaveAsDialog />),
              },
            ]}
            icon={
              <span className="menuSaveIcon">
                <i className="menuIcon las la-save"></i>
                {lastAutobackupCreatedAt && <span className="menuAutobackupIndicator" />}
              </span>
            }
          />
          <SimpleMenuButton
            text="PDF" // no itx
            title={itx("Menubar.projektdokumentationAlsPdf", "Projekt-Dokumentation als PDF erstellen")}
            icon={<LibraryBig className="menuIcon" size={26} strokeWidth={1.4} />}
            onClick={() => setPdfExportOpen(true)}
          />
          <SimpleMenuButton
            id="projectExcelExport"
            text="Excel" // no itx
            title={itx(
              "Menubar.projektlistenAlsExceldokumentation",
              "Projekt-Listen als Excel-Dokumentation erstellen",
            )}
            icon={<FileSpreadsheet className="menuIcon" size={26} strokeWidth={1.4} />}
            onClick={() => setExcelExportOpen(true)}
          />
          {/* {!((Browser.isMobile() || !Browser.isLandScape()) && printIsEnabled()) && (
            <SimpleMenuButton
              command="print" // no itx
              text={state.translate("menuBar.print", "Print")}
              iconName="print" // no itx
              onClick={() => {
                // let id = "";
                // if (state.selectionManager.selectedObject) {
                //   id = state.selectionManager.selectedObject.id;
                // }
                // state.navigate("/print", { objId: id });
                state.navigate("/print");
              }}
              disabled={printIsEnabled()}
            />
          )} */}
          <SimpleMenuButton
            onClick={() => {
              // state.navigate("/settings");

              state.dialogManager.openDialog(<SettingsDialog />, true);
            }}
            command="settings" // no itx
            text={state.translate("menuBar.settings", "Settings")}
            icon={<Settings className="menuIcon" size={26} strokeWidth={1.4} />}
          />

          <SimpleMenuButton
            onClick={() => {
              state.dialogManager.openDialog(<CsvUploadDialog />);
            }}
            command="excelUpload" // no itx
            text={itx("menuBar.excelUpload", "csv Upload")}
            iconName="table" // no itx
          />
        </MenuGroup>

        <MenuGroup title={itx("menuBar.undo", "Rückgängig")}>
          <UndoButton />
          <RedoButton />
          {isUiModeDeveloper() && <UndoDebugButton />}
        </MenuGroup>
        <MenuGroup title={itx("menuBar.clipboard", "Zwischenablage")}>
          {/* <SimpleMenuButton
          onClick={() => {
            onCut();
          }}
          disabled={cutIsDisabled()}
          // disabled={true}
          command="cut"
          text={state.translate("menuBar.cut", "Cut")}
          iconName="cut"
        /> */}

          <SimpleMenuButton
            onClick={() => {
              state.menubarActions.onDelete();
            }}
            disabled={deleteIsDisabled()}
            command="delete" // no itx
            text={state.translate("menuBar.delete", "delete")}
            iconName="trash" // no itx
          />
          <SimpleMenuButton
            onClick={() => {
              state.menubarActions.onCopy();
            }}
            disabled={copyIsDisabled()}
            command="copy" // no itx
            text={state.translate("menuBar.copy", "Copy")}
            iconName="copy" // no itx
          />
          <SimpleMenuButton
            onClick={() => state.menubarActions.onPaste()}
            disabled={pasteIsDisabled()}
            command="paste" // no itx
            text={state.translate("menuBar.paste", "Paste")}
            iconName="paste" // no itx
          />

          {!Browser.isMobile && Browser.isLandScape() && (
            <SimpleMenuButton
              onClick={() => {
                onEdit();
              }}
              disabled={!isEditible()}
              command="edit" // no itx
              text={state.translate("menuBar.edit", "Edit")}
              iconName="edit" // no itx
            />
          )}
        </MenuGroup>

        <MenuGroup title={itx("menuBar.role", "Rolle")}>
          {/* <SimpleMenuButton
            onClick={() => {
              if (isUiModeExpert()) {
                // blabla buigiugugzg
                state.setUiMode(enumUiMode.user);
              } else {
                state.setUiMode(enumUiMode.expert);
              }
            }}
            text={"Expert Mode"}
            icon={
              <i
                style={{
                  color: isUiModeExpert() ? "orange" : undefined,
                  border: isUiModeExpert() ? "1px solid orange" : undefined,
                  borderRadius: "20px",
                  padding: "2px",
                }}
                className={`menuIcon las la-hard-hat`}
              ></i>
            }
          /> */}
          <FlyoutMenuButton
            id={"uiMode"} // no itx
            iconName="hard-hat" // no itx
            content={<UiModeSelection />}
            iconStyle={{
              color: isUiModeExpert() ? "orange" : undefined,
              border: isUiModeExpert() ? "1px solid orange" : undefined, // no itx
              borderRadius: "20px",
              padding: "2px",
            }}
            contextMenuTitle={itx("menubar.changeUserRole", "Benutzerrolle wechseln")}
            contextMenuItems={getUiModeOptions().map((option) => ({
              id: "uiMode_" + option.value, // no itx
              label: option.label,
              iconName:
                option.value == state.uiMode.value
                  ? "check" // no itx
                  : undefined,
              onClick: () => selectUiMode(option.value),
            }))}
          />
        </MenuGroup>

        {isUiModeDeveloper() && (
          <MenuGroup title={itx("menuBar.changes", "Änderungen")}>
            <SimpleMenuButton
              onClick={() => {
                state.manualSetShowModifiedStatus = !state.showModifiedStatus;
              }}
              text={itx("showModified", "Show Modified")}
              iconName="history" // no itx
              pressed={state.showModifiedStatus}
              iconStyle={{ color: state.showModifiedStatus ? "orange" : "grey" }}
            />
            <SimpleMenuButton
              onClick={() => state.clearModifiedStatus()}
              text={itx("menuBar.resetModified", "Reset Modified")}
              iconName="history" // no itx
              iconStyle={{ color: "grey" }}
            />
          </MenuGroup>
        )}

        <C1EditModeMenuGroup />
        {isConnectionPlan(state.activePanes.getActiveItem()?.item?.className) && <C1PlanViewMenuGroup />}

        {isUiModeProductAdmin() && (
          <MenuGroup title={itx("Menubar.consistency", "Consistency")}>
            <SimpleMenuButton
              onClick={() => {
                showConsitencyErrorsDialog(state.consistencyCheckLog);
              }}
              text={"Show"} // no itx
              icon={
                <i
                  style={{
                    borderRadius: "20px",
                    padding: "2px",
                  }}
                  className={`menuIcon las la-check-circle`}
                ></i>
              }
            />
            <SimpleMenuButton
              onClick={() => {
                state.consistencyCheck();
              }}
              text={"Run"} // no itx
              icon={
                <i
                  style={{
                    borderRadius: "20px",
                    padding: "2px",
                  }}
                  className={`menuIcon las la-running`}
                ></i>
              }
            />
          </MenuGroup>
        )}

        {openRoomPlan && getUiMode() != enumUiMode.user && (
          <MenuGroup title={itx("components.Connection", "Verbindung")}>
            <FlyoutMenuButton
              id={"connections"} // no itx
              text={itx("components.plural.Connection", "Verbindung")}
              iconName="bezier-curve" // no itx
              content={<ConnectionSelection />}
              contextMenuTitle={itx("menubar.showConnections", "Verbingungen anzeigen")}
              contextMenuItems={getConnectionModeOptions().map((option) => ({
                id: "connection_" + option.mode, // no itx
                label: option.label,
                iconName: option.mode == state.connectionDisplayMode.value ? "check" : undefined,
                onClick: () => selectConnectionMode(option.mode),
              }))}
            />
          </MenuGroup>
        )}

        {openRoomPlan && (
          <MenuGroup title={state.translate("menuBar.background", "Background ")} id2={`background`}>
            <SimpleMenuButton
              command="bgAdjust" // no itx
              text={state.translate("menuBar.adjust", "Adjust")}
              onClick={() => {
                const page = state.getActivePage();
                if (page instanceof Page) {
                  page.adjustActive.value = !page.adjustActive.value;
                  redraw();
                }
              }}
              icon={
                <i
                  style={{
                    color: state.getActivePage()?.adjustActive.value ? "orange" : undefined,
                    border: state.getActivePage()?.adjustActive.value ? "1px solid orange" : undefined, // no itx
                    borderRadius: "20px",
                    padding: "2px",
                  }}
                  className={`menuIcon las la-expand`}
                ></i>
              }
              disabled={!isAdjustable()}
            />

            {!backgroundImageFromFloorPlan && (
              <SimpleMenuButton
                command="bgRoomSubmittals" // no itx
                text={state.translate("menuBar.submittals", "Submittals")}
                onClick={() => {
                  const page = state.objectRegistry.getObject(state.activePaneId);

                  if (page instanceof Page) {
                    const parent = page?.parent;
                    let showRoomSubmittal = false;
                    if (parent?.className == "Room") {
                      if (
                        (parent.parent as Floor).page?.backgroundImgUrl == undefined ||
                        (parent.parent as Floor).page?.backgroundImgUrl == "" ||
                        (parent.parent as Floor).page == undefined
                      ) {
                        showRoomSubmittal = true;
                      }
                      let pageGroup = new PageGroup();
                      /// get pageGroup

                      if (state.pageGroups.size == 0) {
                        // init state (pageGroup)
                        PageGroup.initPageGroups();
                      }
                      state.pageGroups.forEach((pg) => {
                        if (pg.tag == fixedPageGroupTags.buildingPlans) {
                          pageGroup = pg;
                        }
                      });
                      if (showRoomSubmittal && pageGroup.pages.size != 0) {
                        // no pdfs
                        showRoomSubmittal = false;
                      }
                    }
                    if (showRoomSubmittal) {
                      state.dialogManager.openDialog(<ShowRoomSubmittalsBackgroundInstantSet page={page} />);
                    } else {
                      state.dialogManager.openDialog(<UploadFloorPlansDialog page={page} onChange={() => {}} />);
                    }
                  }
                }}
                iconName="object-group" // no itx
                id="menuButton_submittals"
              />
            )}
          </MenuGroup>
        )}

        {state.getActivePage()?.isFloorPlan && (
          <MenuGroup title={itx("components.plural.Room", "Räume")}>
            <SimpleMenuButton
              command="editRooms" // no itx
              text={state.translate("menuBar.editRooms", "bearbeiten")}
              onClick={() => {
                const page = state.getActivePage();
                if (page?.className == "Page") {
                  state.selectionManager.selectedObject = undefined;
                  page.editRooms = !page.editRooms;
                  page.raiseOnChangeUI("editRooms");
                  state.activePanes.raiseOnChangeUI("editRooms"); // bad trick for Toolbar. // MWE 11.1.2025
                  // connections have to be updated
                  setTimeout(() => {
                    page.updateConnections();
                  }, 100);
                }
              }}
              icon={
                <i
                  style={{
                    color: state.getActivePage()?.editRooms ? "orange" : undefined,
                    border: state.getActivePage()?.editRooms ? "1px solid orange" : undefined, // no itx
                    borderRadius: "20px",
                    padding: "2px",
                  }}
                  className={`menuIcon las la-sign`}
                ></i>
              }
            />
          </MenuGroup>
        )}

        {!mobile && !state.connectionDashboardMode.value && (
          <MenuGroup title={itx("menuBar.tools", "Tools")}>
            <SimpleMenuButton
              onClick={onClickGenerateAdresses}
              command="generateAdress" // no itx
              text={state.translate("menuBar.generateAdress", "Gen Adr")}
              iconName="list-ol" // no itx
            />

            {isUiModeProductAdmin() && (
              <SimpleMenuButton
                onClick={openProductAdminNavigationDialog}
                command="productAdminTools" // no itx
                text={itx("menuBar.productTools", "Products")}
                iconName="boxes" // no itx
                title={itx("menuBar.productToolsTitle", "Open product admin tools")}
              />
            )}

            {/* <SimpleMenuButton
              onClick={openEtsDialog}
              command="ets" // no itx
              text={state.translate("menuBar.syncEts", "Ets")}
              icon={<img style={{ height: "24px" }} src={`${state.hostBaseUrl}images/ets.png`} />}
            /> */}

            {isUiModeDeveloper() && (
              <SimpleMenuButton
                onClick={() => {
                  state.activePanes.openOrActivatePane("etsPostImport"); // no itx
                }}
                command="etsPostImport" // no itx
                text={itx("Menubar.etsimortnachbearbeitung", "Ets Imort Nachbearbeitung")}
                icon={<div> {noItx("PE")} </div>}
              />
            )}

            <SimpleMenuButton
              onClick={openEtsDialog}
              command="ets"
              text={state.translate("menuBar.syncEts", "Ets")}
              icon={
                <div style={{ position: "relative", width: "24px", height: "24px" }} title={etsTitle}>
                  <img style={{ height: "24px", width: "24px" }} src={`${state.hostBaseUrl}images/ets.png`} />
                  <EtsConversionStatusOverlay />
                  <EtsPushStatusOverlay />
                </div>
              }
            />

            {/* <SimpleMenuButton
              onClick={openEtsDialog}
              command="ets"
              text={state.translate("menuBar.syncEts", "Ets")}
              icon={
                <div style={{ position: "relative", width: "24px", height: "24px" }}>
                  <img style={{ height: "24px", width: "24px" }} src={`${state.hostBaseUrl}images/ets.png`} />
                  <StatusOverlay exportState={state.projectMetadata.exportState} />
                </div>
              }
            /> */}

            {/* {browserName != "safari" && isUiModeDeveloper() && (
              <SimpleMenuButton
                onClick={openEtsDiagnosticViewer}
                command="diagnostics" // no itx
                text={state.translate("groupMonitor.groupDiagnostics", "Group Diagnostics")}
                iconName="desktop" // no itx
                disabled={false}
                // status={etsIsOnline}
                title={
                  etsIsOnline
                    ? ""
                    : state.translate("groupMonitor.enableEts", "Please run ETS to use this functinality.")
                }
              />
            )} */}

            {browserName != "safari" && isUiModeDeveloper() && (
              <SimpleMenuButton
                onClick={openDiagnosticViewer}
                command="diagnostics" // no itx
                text={state.translate("groupMonitor.networkDiagnostics", "Network Diagnostics")}
                iconName="desktop" // no itx
                disabled={false}
              />
            )}

            {/* {browserName != "safari" && isUiModeDeveloper() && (
              <SimpleMenuButton
                onClick={openReadbackMemoryViewer}
                command="readbackTest" // no itx
                text={state.translate("groupMonitor.readbackTest", "Readback Test")}
                iconName="exchange" // no itx
                disabled={false}
              />
            )} */}

            {isBetaVersion() && isUiModeProductAdmin() && (
              <SimpleMenuButton
                onClick={openProgrammingPane}
                text={itx("Menubar.programming", "Programming")}
                iconName="microchip" // no itx
                title={itx("inlineButton.progDevices", "Device Programming")}
              />
            )}

            {/* MWE 31.07.2025: To be released in a future version */}
            {isUiModeDeveloper() && (
              <SimpleMenuButton
                onClick={openReadbackDeviceDialog}
                text={itx("Menubar.readback", "Readback")}
                iconName="exchange-alt" // no itx
                title={itx("inlineButton.readbackDevice", "Readback Device")}
              />
            )}

            {/* {browserName != "safari" && (
              <SimpleMenuButton
                onClick={() => {
                  try {
                    state.settings.diagnosticMode = !state.settings.diagnosticMode;
                    state.settings.raiseOnChange();
                    setDiagnosticInProgress(state.settings.diagnosticMode);
                    KnxBridge.start();
                    if (state.settings.diagnosticMode) {
                      showToast({
                        text: state.translate("groupMonitor.diagnosticsModeOn", "Diagnostics Started."),
                      });
                    } else {
                      showToast({
                        text: state.translate("groupMonitor.diagnosticsModeOff", "Diagnostics Stopped."),
                      });
                    }
                  } catch (ex) {
                    showToast({
                      text: state.translate(
                        "groupMonitor.unknownError",
                        "Something went wrong while trying to change to Diagnostics mode. Please try again later.",
                      ),
                    });
                  }
                }}
                command="diagnosticTrigger"
                text={
                  diagnosticInProgress
                    ? state.translate("groupMonitor.stopDiagnostics", "Stop Diagnostics")
                    : state.translate("groupMonitor.startDiagnostics", "Start Diagnostics")
                }
                iconName={diagnosticInProgress ? "stop" : "play"}
                disabled={false}
                status={true} //etsIsOnline
                title={
                  // etsIsOnline ?
                  diagnosticInProgress
                    ? state.translate("groupMonitor.stop", "Stop")
                    : state.translate("groupMonitor.start", "Start")
                  // : state.translate("groupMonitor.enableEts", "Please run ETS to use this functionality.")
                }
              />
            )} */}
          </MenuGroup>
        )}

        <MenuGroup
          title={itx("menuBar.assistants", "Assistants")}
          style={mobile ? {} : { width: "100px" }} // title isn`t shown correct
          // title={""}
        >
          {isUiModeDeveloper() && (
            <SimpleMenuButton
              iconName="brain" // no itx
              text={itx("menuBar.assistent") + " only Dev"} // no itx
              onClick={() => {
                state.activePanes.openOrActivatePane("ai"); // no itx
              }}
            />
          )}

          <SimpleMenuButton
            icon={
              <img
                style={{ width: "24px", height: "24px" }}
                src={`${state.hostBaseUrl}images/connection-dashboard.svg`}
                alt=""
              />
            }
            text="Connection Dashboard" // no itx
            title="Connection Dashboard"
            pressed={state.connectionDashboardMode.value}
            onClick={() => {
              state.connectionDashboardMode.value = !state.connectionDashboardMode.value;
            }}
          />
          <SimpleMenuButton
            iconName="terminal" // no itx
            text={itx("menuBar.commander", "Commander")}
            onClick={() => {
              state.activePanes.openOrActivatePane("commander"); // no itx
            }}
          />
        </MenuGroup>

        {isUiModeDeveloper() && (
          <MenuGroup title={itx("menuBar.system", "System")}>
            <SimpleMenuButton
              onClick={openDebugDialog}
              command="debug" // no itx
              text="Debug" // no itx
              iconName="hammer" // no itx
              disabled={false}
              title={itx("menuBar.debug")}
            />
            <SimpleMenuButton
              onClick={async () => {
                const { openRenderStormTestDialog } = await import("./dialogs/RenderStormTestDialog");
                openRenderStormTestDialog();
              }}
              command="reactRenderStormTest" // no itx
              text="React Storm" // no itx
              iconName="bolt" // no itx
              title="React-Watchdog mit einem absichtlichen Render-Storm testen"
            />

            {/* <SimpleMenuButton
              title="allSignalTest" // no itx
              onClick={() => {
                state.dialogManager.openDialog(<SignalTestDialog />);
              }}
            /> */}
          </MenuGroup>
        )}

        {!state.connectionDashboardMode.value && isBetaVersion() && isUiModeDeveloper() && (
          <MenuGroup
            style={mobile ? {} : { width: "100px" }} // title isn`t shown correct
            title={itx("menuBar.panel", "Panel")}
          >
            <SimpleMenuButton
              onClick={async () => {
                const showPanelKeyDialog: any = (await import("./ExportPanel"))?.default;
                showPanelKeyDialog && showPanelKeyDialog();
              }}
              command="panel" // no itx
              text={state.translate("menuBar.preview", "Vorschau")}
              icon={<i className={`menuIcon las la-rotate-90 la-tablet`}></i>}
            />
          </MenuGroup>
        )}

        {!state.connectionDashboardMode.value && isBetaVersion() && isUiModeDeveloper() && (
          <MenuGroup
            style={mobile ? {} : { width: "100px" }} // title isn`t shown correct
            title={itx("menuBar.install", "Install")}
          >
            <SimpleMenuButton
              href={!state.estadaBridgeIsRunning ? "estada://Test" : undefined}
              onClick={async () => {
                state.justTiedToStartEstadaBridge = true;
                state.dialogManager.openDialog(<InstallDialog />);
                // state.settings.diagnosticMode = !state.settings.diagnosticMode;
                // if (state.settings.diagnosticMode) {
                //   InstallationUtils.init();
                // }
                // state.settings.raiseOnChange();
              }}
              command="panel" // no itx
              text={state.settings.diagnosticMode ? itx("menubar.off", "Aus") : itx("Menubar.ein", "Ein")}
              icon={
                <div
                  style={{
                    padding: "2px",
                    borderRadius: "5px",
                    backgroundColor: state.settings.diagnosticMode ? "lightGreen" : undefined, // no itx
                  }}
                >
                  <i className={`menuIcon las la-tasks`}></i>
                </div>
              }
            />
          </MenuGroup>
        )}

        {!state.connectionDashboardMode.value && isUiModeExpert() && <Menubar_Extensions />}

        {!state.connectionDashboardMode.value && (
          <MenuGroup
            style={mobile ? {} : { width: "100px" }} // title isn`t shown correct
            title={state.translate("menuBar.help", "Hilfe")}
          >
            <SimpleMenuButton
              id="menu_guide"
              iconName="robot" // no itx
              text={itx("menubar.guide", "Guide")}
              onClick={() => {
                state.guideMode.onReopenFromMenu();
              }}
            />
            <SimpleMenuButton
              iconName="question" // no itx
              text={itx("menuBar.help", "Hilfe")}
              onClick={() => {
                if (
                  state.activePanes.isOpen("help") // no itx
                ) {
                  state.activePanes.activatePane("help"); // no itx
                } else {
                  state.activePanes.loadPage("help"); // no itx
                }

                // window.open("https://estada.de/hilfe", "_blank");
              }}
            />
          </MenuGroup>
        )}

        <div style={{ marginLeft: "auto" }}></div>
        <CheckBrowser>
          {!mobile && (
            <MenuGroup
              style={{
                float: "right",
                paddingLeft: "25px",
                paddingRight: "25px",
                curser: "pointer",
              }}
            >
              <SimpleMenuButton
                iconName="question" // no itx
                text="Release Notes" // no itx
              >
                <div
                  style={{
                    textAlign: "center",
                    fontSize: "0.7em",
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "center",
                    whiteSpace: "nowrap",
                    paddingTop: "5px",
                    cursor: "pointer",
                  }}
                  onClick={() => {
                    if (
                      state.activePanes.isOpen("releaseNotes") // no itx
                    ) {
                      state.activePanes.activatePane("releaseNotes"); // no itx
                    } else {
                      state.activePanes.loadPage("releaseNotes"); // no itx
                    }
                  }}
                >
                  <div>{__APP_VERSION__}</div>
                  <div style={{ fontSize: "0.75em" }}>{__APP_DATE__}</div>
                </div>
              </SimpleMenuButton>
            </MenuGroup>
          )}
        </CheckBrowser>
      </div>
    </div>
  );
}
